import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { authService } from '../../services/authService';

// Async thunks
export const loginUser = createAsyncThunk(
  'auth/login',
  async ({ login, password, referralCode }, { rejectWithValue }) => {
    try {
      const response = await authService.login({ login, password });
      
      if (referralCode && !response.user.referredBy) {
        try {
          await authService.applyReferralCode(referralCode);
        } catch (refError) {
          console.error('Failed to apply referral code:', refError);
        }
      }
      
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: error.message });
    }
  }
);

export const registerUser = createAsyncThunk(
  'auth/register',
  async ({ userData, referralCode }, { rejectWithValue }) => {
    try {
      const response = referralCode
        ? await authService.registerWithReferral(referralCode, userData)
        : await authService.register(userData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: error.message });
    }
  }
);

export const verifyPhone = createAsyncThunk(
  'auth/verifyPhone',
  async ({ phone, code }, { rejectWithValue }) => {
    try {
      const response = await authService.verifyPhone(phone, code);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: error.message });
    }
  }
);

export const applyReferralCode = createAsyncThunk(
  'auth/applyReferralCode',
  async (referralCode, { rejectWithValue }) => {
    try {
      const response = await authService.applyReferralCode(referralCode);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: error.message });
    }
  }
);

const initialState = {
  user: authService.getCurrentUser(),
  token: localStorage.getItem('token'),
  loading: {
    login: false,
    register: false,
    phoneVerification: false,
    referralCode: false
  },
  error: {
    login: null,
    register: null,
    phoneVerification: null,
    referralCode: null
  },
  isAuthenticated: !!localStorage.getItem('token'),
  phoneVerified: false,
  referralStats: {
    totalReferrals: 0,
    activeReferrals: 0,
    totalPoints: 0
  }
};

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    updateUser: (state, action) => {
      // Read current user from localStorage to preserve all fields
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      // Merge stored user with Redux state and new payload
      state.user = { ...storedUser, ...state.user, ...action.payload };
      // Explicitly preserve critical fields
      if (storedUser.id) state.user.id = storedUser.id;
      if (storedUser.role) state.user.role = storedUser.role;
      localStorage.setItem('user', JSON.stringify(state.user));
    },
    updateReferralStats: (state, action) => {
      state.referralStats = { ...state.referralStats, ...action.payload };
    },
    clearErrors: (state) => {
      state.error = {
        login: null,
        register: null,
        phoneVerification: null,
        referralCode: null
      };
    },
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.phoneVerified = false;
      state.referralStats = {
        totalReferrals: 0,
        activeReferrals: 0,
        totalPoints: 0
      };
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    },
  },
  extraReducers: (builder) => {
    // Login
    builder.addCase(loginUser.pending, (state) => {
      state.loading.login = true;
      state.error.login = null;
    });
    builder.addCase(loginUser.fulfilled, (state, action) => {
      state.loading.login = false;
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.isAuthenticated = true;
      state.phoneVerified = action.payload.user.phoneVerified;
    });
    builder.addCase(loginUser.rejected, (state, action) => {
      state.loading.login = false;
      state.error.login = action.payload?.error || 'Login failed';
    });

    // Register
    builder.addCase(registerUser.pending, (state) => {
      state.loading.register = true;
      state.error.register = null;
    });
    builder.addCase(registerUser.fulfilled, (state, action) => {
      state.loading.register = false;
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.isAuthenticated = true;
      state.phoneVerified = action.payload.user.phoneVerified;
    });
    builder.addCase(registerUser.rejected, (state, action) => {
      state.loading.register = false;
      state.error.register = action.payload?.error || 'Registration failed';
    });

    // Phone Verification
    builder.addCase(verifyPhone.pending, (state) => {
      state.loading.phoneVerification = true;
      state.error.phoneVerification = null;
    });
    builder.addCase(verifyPhone.fulfilled, (state, action) => {
      state.loading.phoneVerification = false;
      state.phoneVerified = true;
      if (state.user) {
        state.user.phoneVerified = true;
      }
    });
    builder.addCase(verifyPhone.rejected, (state, action) => {
      state.loading.phoneVerification = false;
      state.error.phoneVerification = action.payload?.error || 'Phone verification failed';
    });

    // Apply Referral Code
    builder.addCase(applyReferralCode.pending, (state) => {
      state.loading.referralCode = true;
      state.error.referralCode = null;
    });
    builder.addCase(applyReferralCode.fulfilled, (state, action) => {
      state.loading.referralCode = false;
      if (action.payload.user) {
        state.user = action.payload.user;
      }
    });
    builder.addCase(applyReferralCode.rejected, (state, action) => {
      state.loading.referralCode = false;
      state.error.referralCode = action.payload?.error || 'Failed to apply referral code';
    });
  },
});

export const { 
  updateUser, 
  updateReferralStats, 
  clearErrors, 
  logout 
} = authSlice.actions;

// Selectors
export const selectUser = (state) => state.auth.user;
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
export const selectIsPhoneVerified = (state) => state.auth.phoneVerified;
export const selectAuthLoading = (state) => state.auth.loading;
export const selectAuthError = (state) => state.auth.error;
export const selectReferralStats = (state) => state.auth.referralStats;

export default authSlice.reducer;